import { type RefObject, useEffect } from "react";

/** Window events after which an open menu no longer matches the page. */
const DISMISSING_WINDOW_EVENTS = ["resize", "blur", "popstate", "hashchange"] as const;

/**
 * Calls `onDismiss` on a pointer down or a scroll outside `layerRef`, and on resize, window
 * blur and history navigation, while the calling component is mounted.
 */
export function useDismiss(layerRef: RefObject<HTMLElement | null>, onDismiss: () => void): void {
  useEffect(() => {
    const inside = (t: EventTarget | null): boolean => t instanceof Node && Boolean(layerRef.current?.contains(t));
    const onOutside = (e: Event): void => {
      if (!inside(e.target)) onDismiss();
    };
    const onAny = (): void => onDismiss();
    document.addEventListener("pointerdown", onOutside, true);
    window.addEventListener("scroll", onOutside, true);
    for (const type of DISMISSING_WINDOW_EVENTS) window.addEventListener(type, onAny);
    return () => {
      document.removeEventListener("pointerdown", onOutside, true);
      window.removeEventListener("scroll", onOutside, true);
      for (const type of DISMISSING_WINDOW_EVENTS) window.removeEventListener(type, onAny);
    };
  }, [layerRef, onDismiss]);
}
